export interface ExtractedMapItem {
  /** Inline form: parsed FeatureCollection from the tag body. */
  geojson?: GeoJSON.FeatureCollection;
  /** The tag was present but its body was not a FeatureCollection (or its
   *  src was invalid). Rendered as a small "map unavailable" note so the user
   *  sees that a map was intended, instead of raw markup or nothing. */
  error?: string;
  /** Reference form: opaque relative path from <geojson src="output/x.geojson">,
   *  or an absolute presigned URL in shared-conversation views. Fetched + rendered
   *  by GeoJsonMapLoader (large tool-produced geometry never travels through the
   *  model inline). */
  src?: string;
  title?: string;
}

export interface ExtractedMapData {
  maps: ExtractedMapItem[];
  originalMessage: string;
}

// One tag regex for BOTH forms — attributes captured as a blob and parsed
// separately so their order never matters, body optional (self-closing or
// empty-body reference tags):
//   <geojson title="...">{...inline json...}</geojson>
//   <geojson src="output/x.geojson" title="..."></geojson>  (or <geojson src="..."/>)
// The attrs blob is quote-aware ((?:[^>"]|"[^"]*")*?) so a `>` inside a quoted
// title (e.g. title="Flow > 100") doesn't truncate the match and leak raw markup.
// Single-pass replace() extraction — an exec() loop over a mutating string
// silently drops the 2nd+ tag (see the note in imageUtils.ts).
const GEOJSON_TAG_REGEX = /<geojson\b((?:[^>"]|"[^"]*")*?)(?:\/>|>([\s\S]*?)<\/geojson>)/gi;

const ATTR_SRC_REGEX = /\bsrc="([^"]*)"/i;
const ATTR_TITLE_REGEX = /\btitle="([^"]*)"/i;

// The src is an opaque token under the conversation's output/ prefix, exactly
// like <graph> paths. Shared-conversation snapshots rewrite it to an absolute
// presigned S3 URL at view time, so https URLs pass through too — toPublicUrl()
// host-allowlists those (same posture as <graph>/<outimg> paths). Everything
// else (traversal, wrong extension, non-https schemes) is rejected here.
const SRC_PATH_REGEX = /^output\/[A-Za-z0-9._-]+\.geojson$/;
const SRC_HTTPS_REGEX = /^https:\/\//i;

// Parse an inline tag body as a FeatureCollection. The model occasionally leaves
// junk between the JSON and the closing tag (seen: `...}}]}></geojson>`), so a
// failed parse retries on the substring up to the last `}` before giving up.
function parseFeatureCollection(body: string): GeoJSON.FeatureCollection | null {
  const attempt = (text: string): GeoJSON.FeatureCollection | null => {
    try {
      const parsed = JSON.parse(text);
      if (parsed && parsed.type === 'FeatureCollection' && Array.isArray(parsed.features)) {
        return parsed as GeoJSON.FeatureCollection;
      }
    } catch {
      // fall through
    }
    return null;
  };
  const trimmed = body.trim();
  const direct = attempt(trimmed);
  if (direct) return direct;
  const lastBrace = trimmed.lastIndexOf('}');
  if (lastBrace > 0 && lastBrace < trimmed.length - 1) {
    return attempt(trimmed.slice(0, lastBrace + 1));
  }
  return null;
}

// Helper to find JSON objects in text by tracking braces
function findJSONBlocks(text: string): string[] {
  const blocks: string[] = [];
  let depth = 0;
  let startIdx = -1;

  for (let i = 0; i < text.length; i++) {
    if (text[i] === '{') {
      if (depth === 0) {
        startIdx = i;
      }
      depth++;
    } else if (text[i] === '}') {
      depth--;
      if (depth === 0 && startIdx !== -1) {
        const candidate = text.substring(startIdx, i + 1);
        blocks.push(candidate);
        startIdx = -1;
      }
    }
  }

  return blocks;
}

export function extractGeoJSONFromMessage(message: string): ExtractedMapData | null {
  const maps: ExtractedMapItem[] = [];

  // Single pass: collect map entries and strip every tag (valid or not — a
  // malformed tag must not leak raw JSON/markup into the displayed text).
  let originalMessage = message.replace(
    GEOJSON_TAG_REGEX,
    (_full: string, attrs: string, body: string | undefined) => {
      const title = ATTR_TITLE_REGEX.exec(attrs || '')?.[1] || undefined;
      const src = ATTR_SRC_REGEX.exec(attrs || '')?.[1];

      if (src !== undefined) {
        // Reference form. An invalid path gets an error entry (tag still
        // stripped) — the loader would only produce an error card for it anyway.
        const trimmed = src.trim();
        if (SRC_PATH_REGEX.test(trimmed) || SRC_HTTPS_REGEX.test(trimmed)) {
          maps.push({ src: trimmed, title });
        } else {
          maps.push({ title, error: 'Map unavailable — invalid geometry reference.' });
        }
        return '';
      }

      const geojson = parseFeatureCollection(body || '');
      if (geojson) {
        maps.push({ geojson, title });
      } else {
        // A matched-but-unparseable tag MUST still count as extracted: returning
        // null here made the caller fall back to the unstripped message and the
        // raw <geojson …> markup rendered as prose.
        maps.push({ title, error: 'Map unavailable — the map data in this answer could not be read.' });
      }
      return '';
    }
  );

  // If no tags matched at all, try to find raw (untagged) GeoJSON — legacy
  // fallback for older messages.
  if (maps.length === 0 && originalMessage === message) {
    const jsonBlocks = findJSONBlocks(message);

    for (const block of jsonBlocks) {
      try {
        const parsed = JSON.parse(block);
        if (parsed && parsed.type === 'FeatureCollection' && Array.isArray(parsed.features)) {
          maps.push({ geojson: parsed as GeoJSON.FeatureCollection, title: undefined });
          originalMessage = originalMessage.replace(block, '');
        }
      } catch {
        // Not valid JSON or not GeoJSON, skip
      }
    }
  }

  if (maps.length === 0) return null;

  return { maps, originalMessage: originalMessage.trim() };
}
